import { zLogRocket, ZLogRocket } from '@zoll/logrocket-vue3-lib';
import { postConstruct } from 'inversify';

import { service } from '@/decorators/service';
import { ProxyClass } from '@/utils/ProxyClass';

export const sanitizers = zLogRocket.utilities.createSanitizers();

// List of properties to sanitize, when they have an exact and case-sensitive match.
sanitizers.anywhere.sanitizeExactMatch = ['Authorization'];

// List of properties to sanitize, when they have a partial and case-insensitive match.
sanitizers.anywhere.sanitizeFuzzyMatch = [
  'governmentId',
  'name',
  'middle',
  'phone',
  'birth',
  'dob',
  'addr',
  'zip',
  'city',
  'state',
  'email',
  'address',
  'race',
  'gender',
  'employer',
];

// List of properties to ignore and not sanitize, when they are recursively, exact and case-sensitive matched.
sanitizers.anywhere.ignoreExactMatch = [];

type TrackEventPropertyValue = string | number | boolean | string[] | number[] | boolean[] | undefined;

type TrackEventProperties = {
  revenue?: number,
  [key: string]: TrackEventPropertyValue
};

export interface ILogRocket extends ZLogRocket {
  track(eventName: string, eventProperties?: TrackEventProperties): void;
}

@service(Logger)
export class Logger extends ProxyClass(zLogRocket, 'LogRocket') implements ILogRocket {
  /**
   * When true, all track() calls are also logged to console.debug.
   * Enabled via URL param `?perfdebug=1` or `localStorage.setItem('perfdebug', '1')`.
   */
  private readonly debugToConsole: boolean = Logger.hasDebugFlag();

  private static hasDebugFlag(): boolean {
    if (import.meta.env.PROD) return false;
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get('perfdebug') === '1') return true;
      return window.localStorage.getItem('perfdebug') === '1';
    }
    catch {
      return false;
    }
  }

  @postConstruct()
  // LogRocket types are messed up, ignore warning.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  initialize() {
    this.$target.initialize({
      appId: 'zoll-data/zoll-dispatch',
      sanitizers,
      // Additional LogRocket configuration options.
      // @see https://docs.logrocket.com/reference/init
      options: {
        release: PACKAGE_VERSION,
      },
    });
  }

  // Methods.
  track(eventName: string, eventProperties?: TrackEventProperties) {
    const isSimple = (v: unknown): boolean => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

    // Ensure any object passed is properly flattened, but do not modify the original object.
    // @todo VUE3 - Move this upstream into @zoll/logrocket-base-lib.
    const obj: TrackEventProperties = {};
    if (eventProperties) {
      Object.keys(eventProperties).forEach((key) => {
        const value = eventProperties[key];
        if (typeof value === 'undefined' || isSimple(value) || (Array.isArray(value) && [...value].every(isSimple))) {
          obj[key] = value;
        }
        else {
          obj[key] = JSON.stringify(value);
        }
      });
    }

    // Log to console when perfdebug is enabled (dev performance testing)
    if (this.debugToConsole) {
      // eslint-disable-next-line no-console
      console.debug(`[track] ${eventName}`, Object.keys(obj).length ? obj : '');
    }

    this.instance.track(eventName, Object.keys(obj).length ? obj : undefined);
  }
}
